import * as i0 from '@angular/core';
import { inject, Injectable, NgModule } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import * as i1 from '@ngrx/effects';
import { Actions, createEffect, ofType, EffectsModule } from '@ngrx/effects';
import { createAction, props, createReducer, on, combineReducers, createFeatureSelector, createSelector, Store, select } from '@ngrx/store';
import { map, tap, withLatestFrom, filter, retryWhen, mergeMap, delay, catchError, switchMap, exhaustMap, mapTo, takeUntil, first } from 'rxjs/operators';
import { defer, of, iif, throwError, timer, merge } from 'rxjs';
import { HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Manages cookies Titan uses
 * @docs-public
 * */
class UserCookieService {
    constructor() {
        this.cookieService = inject(CookieService);
    }
    get groups() {
        const titanCookie = this.getTitanCookie();
        if (titanCookie) {
            return titanCookie.groups;
        }
        return [];
    }
    getTitanCookie() {
        const titanCookie = this.cookieService.get('titan');
        /*
         Previous ngx-cookie uses JSON stringify and JSON parse under the hood
         https://github.com/salemdar/ngx-cookie/blob/master/projects/ngx-cookie/src/lib/cookie.service.ts
        */
        return titanCookie === '' ? undefined : JSON.parse(titanCookie);
    }
    getCsrf() {
        /* make undefined if empty string for backwards compatibility */
        return this.cookieService.get('zeus-csrf-token') || undefined;
    }
    removeCsrf() {
        this.cookieService.delete('zeus-csrf-token');
    }
    hasCsrf() {
        return this.cookieService.check('zeus-csrf-token');
    }
    setTitanCookie(object) {
        if (Array.isArray(object['mail']) && object['mail'].length) {
            object = { ...object, emails: object['mail'], mail: object['mail'][0] };
        }
        // Backwards compatibility
        // Titan did this, and some widgets / apps depend on 'name'
        if (object['cn']) {
            object = { ...object, name: object['cn'] };
        }
        /*
         Previous ngx-cookie uses JSON stringify and JSON parse under the hood
         https://github.com/salemdar/ngx-cookie/blob/master/projects/ngx-cookie/src/lib/cookie.service.ts
        */
        this.cookieService.set('titan', JSON.stringify(object));
    }
    /** Set titan user locale. Will expire in 14 days and be applied to root path */
    setLocale(locale) {
        this.cookieService.set('titan_loc', locale, 14, '/', undefined, true);
    }
    getLocale() {
        /* make undefined if empty string for backwards compatibility */
        return this.cookieService.get('titan_loc') || undefined;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: UserCookieService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: UserCookieService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: UserCookieService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class UserCookieModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: UserCookieModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.2", ngImport: i0, type: UserCookieModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: UserCookieModule, providers: [UserCookieService] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: UserCookieModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [UserCookieService],
                }]
        }] });

const login = createAction('[User] Login', props());
const loginSuccess = createAction('[User] Login Success', props());
const loginFailed = createAction('[User] Login Failed', props());
const loginAloyInitiate = createAction('[User] Initiate Login with Aloy', props());
const loginAloy = createAction('[User] Login with Aloy', props());
const logout = createAction('[User] Logout');
const logoutSuccess = createAction('[User] Logout Success');
const logoutFailed = createAction('[User] Logout Failed', props());
const authenticate = createAction('[User] Authenticate');
const authenticateSuccess = createAction('[User] Authenticate Success', props());
const authenticateFailed = createAction('[User] Authenticate Failed', props());
const setLocale = createAction('[User] Set Locale', props());
const setLocaleSuccess = createAction('[User] Set Locale Success', props());
const setLocaleCookie = createAction('[User] Set Locale Cookie', props());
const setLocaleCookieSuccess = createAction('[User] Set Locale Cookie Success', props());
const setTitanCookie = createAction('[User] Set Titan Cookie', props());
const setTitanCookieSuccess = createAction('[User] Set Titan Cookie Success');
const getAdsUser = createAction('[User] Get ADS User', props());
const getAdsUserSuccess = createAction('[User] Get ADS User Success', props());
const getAdsUserFailed = createAction('[User] Get ADS User Failed', props());
const createAdsUser = createAction('[User] Create ADS User', props());
const createAdsUserSuccess = createAction('[User] Create ADS User Success', props());
const createAdsUserFailed = createAction('[User] Create ADS User Failed', props());
const adsUserSocketUpdate = createAction('[User] ADS User Socket Update', props());
const updateAdsUser = createAction('[User] Update ADS User', props());
const updateAdsUserSuccess = createAction('[User] Update ADS User Success', props());
const updateAdsUserFailed = createAction('[User] Update ADS User Failed', props());
const updateAdsUserLocale = createAction('[User] Update ADS User Locale', props());
const sendHeartbeat = createAction('[User] Send Heartbeat');
const sendHeartbeatSuccess = createAction('[User] Send Heartbeat Success');
const sendHeartbeatFailed = createAction('[User] Send Heartbeat Failed', props());
const sendHeartbeatUnauthorized = createAction('[User] Send Heartbeat Unauthorized');
const toggleTheme = createAction('[User] Toggle Theme');
const updateTheme = createAction('[User] Update Theme', props());
const updateAdsTheme = createAction('[User] Update Ads User Theme', props());
const loadInitialTheme = createAction('[User] Load Initial Theme');
const updateLocalStorageTheme = createAction('[User] Update Local Storage Theme', props());
const updateLocalStorageThemeSuccess = createAction('[User] Update Local Storage Theme Success', props());
const setUsername = createAction('[User] Set Username', props());
const setRecentlyViewedDashboard = createAction('[User] Set Recently Viewed Dashboard', props());
const setRecentlyViewedApplication = createAction('[User] Set Recently Viewed Application', props());
const deleteRecentlyViewedDashboards = createAction('[User] Delete Recently Viewed Dashboards', props());
const updateDashboardTitleInRecentlyViewed = createAction('[User] Update Dashboard Title In Recently Viewed', props());
const setLandingUrlToSessionStorageAction = createAction('[User] Set Landing Url To Session Storage');
const deleteRecentlyViewedApplication = createAction('[User] Delete Recently Viewed Application', props());
const addPinnedApplication = createAction('[User] Add App To Pinned Applications', props());
const deletePinnedApplication = createAction('[User] Delete App From Pinned Applications', props());
/** @deprecated  Use action creators directly instead **/
const UserActionTypes = {
    /** @deprecated  Use action creators directly instead **/
    Login: login.type,
    /** @deprecated  Use action creators directly instead **/
    LoginSuccess: loginSuccess.type,
    /** @deprecated  Use action creators directly instead **/
    LoginFailed: loginFailed.type,
    /** @deprecated  Use action creators directly instead **/
    Logout: logout.type,
    /** @deprecated  Use action creators directly instead **/
    LogoutSuccess: logoutSuccess.type,
    /** @deprecated  Use action creators directly instead **/
    LogoutFailed: logoutFailed.type,
    /** @deprecated  Use action creators directly instead **/
    Authenticate: authenticate.type,
    /** @deprecated  Use action creators directly instead **/
    AuthenticateSuccess: authenticateSuccess.type,
    /** @deprecated  Use action creators directly instead **/
    AuthenticateFailed: authenticateFailed.type,
    /** @deprecated  Use action creators directly instead **/
    SetLocale: setLocale.type,
    /** @deprecated  Use action creators directly instead **/
    SetLocaleSuccess: setLocaleSuccess.type,
    /** @deprecated  Use action creators directly instead **/
    SetLocaleCookie: setLocaleCookie.type,
    /** @deprecated  Use action creators directly instead **/
    SetLocaleCookieSuccess: setLocaleCookieSuccess.type,
    /** @deprecated  Use action creators directly instead **/
    CreateAdsUser: createAdsUser.type,
    /** @deprecated  Use action creators directly instead **/
    CreateAdsUserSuccess: createAdsUserSuccess.type,
    /** @deprecated  Use action creators directly instead **/
    CreateAdsUserFailed: createAdsUserFailed.type,
    /** @deprecated  Use action creators directly instead **/
    SetTitanCookie: setTitanCookie.type,
    /** @deprecated  Use action creators directly instead **/
    SetTitanCookieSuccess: setTitanCookieSuccess.type,
    /** @deprecated  Use action creators directly instead **/
    AdsUserSocketUpdate: adsUserSocketUpdate.type,
    /** @deprecated  Use action creators directly instead **/
    GetAdsUser: getAdsUser.type,
    /** @deprecated  Use action creators directly instead **/
    GetAdsUserSuccess: getAdsUserSuccess.type,
    /** @deprecated  Use action creators directly instead **/
    GetAdsUserFailed: getAdsUserFailed.type,
    /** @deprecated  Use action creators directly instead **/
    UpdateAdsUser: updateAdsUser.type,
    /** @deprecated  Use action creators directly instead **/
    UpdateAdsUserSuccess: updateAdsUserSuccess.type,
    /** @deprecated  Use action creators directly instead **/
    UpdateAdsUserFailed: updateAdsUserFailed.type,
    /** @deprecated  Use action creators directly instead **/
    UpdateAdsUserLocale: updateAdsUserLocale.type,
    /** @deprecated  Use action creators directly instead **/
    SendHeartbeat: sendHeartbeat.type,
    /** @deprecated  Use action creators directly instead **/
    SendHeartbeatSuccess: sendHeartbeatSuccess.type,
    /** @deprecated  Use action creators directly instead **/
    SendHeartbeatFailed: sendHeartbeatFailed.type,
    /** @deprecated  Use action creators directly instead **/
    SendHeartbeatUnauthorized: sendHeartbeatUnauthorized.type,
    /** @deprecated  Use action creators directly instead **/
    ToggleTheme: toggleTheme.type,
    /** @deprecated  Use action creators directly instead **/
    SetUsername: setUsername.type,
    /** @deprecated  Use action creators directly instead **/
    SetRecentlyViewedDashboard: setRecentlyViewedDashboard.type,
    /** @deprecated  Use action creators directly instead **/
    SetRecentlyViewedApplication: setRecentlyViewedApplication.type,
    /** @deprecated  Use action creators directly instead **/
    DeleteRecentlyViewedDashboards: deleteRecentlyViewedDashboards.type,
    /** @deprecated  Use action creators directly instead **/
    UpdateDashboardTitleInRecentlyViewed: updateDashboardTitleInRecentlyViewed.type,
    /** @deprecated  Use action creators directly instead **/
    SetLandingUrlToSessionStorage: setLandingUrlToSessionStorageAction.type,
    /** @deprecated  Use action creators directly instead **/
    DeleteRecentlyViewedApplication: deleteRecentlyViewedApplication.type,
};

const initialState$5 = false;
const reducer$5 = createReducer(initialState$5, on(authenticateSuccess, () => true), on(logout, () => false), on(sendHeartbeatUnauthorized, () => false));

const initialState$4 = 'en';
const reducer$4 = createReducer(initialState$4, on(setLocale, (state, action) => action.payload));

/* eslint-disable @typescript-eslint/no-unused-vars */
const initialState$3 = undefined;
const maxNumberPagesToSave = 5;
const addAndMoveToFront = (comparator) => (value, values = [], maxElements = Infinity) => [value, ...values].reduce((acc, element) => {
    if (acc.length === maxElements) {
        return acc;
    }
    const elementAlreadyExists = acc.some(a => comparator(a, element));
    if (elementAlreadyExists) {
        return acc;
    }
    return [...acc, element];
}, []);
const addAndMoveToBack = (comparator) => (value, values = [], maxElements = Infinity) => [...values, value].reduce((acc, element) => {
    if (acc.length === maxElements) {
        return acc;
    }
    const elementAlreadyExists = acc.some(a => comparator(a, element));
    if (elementAlreadyExists) {
        return acc;
    }
    return [...acc, element];
}, []);
const reducer$3 = createReducer(initialState$3, on(adsUserSocketUpdate, (state, { payload: { _revision, _hash, _timestamp, _id, ...user } }) => ({
    ...user,
    ...(state ? { _perm: state._perm } : undefined),
})), on(getAdsUserSuccess, (state, { payload: { _revision, _hash, _timestamp, _id, ...user } }) => ({
    ...user,
})), on(updateAdsUserLocale, (state, { payload: locale }) => !state
    ? state
    : {
        ...state,
        locale,
    }), on(updateAdsTheme, (state, action) => !state ? state : { ...state, lightTheme: action.theme === 'light-theme', themeModifiedAt: action.themeModifiedAt }), on(setRecentlyViewedDashboard, (state, action) => {
    if (!state) {
        return state;
    }
    const addRecentlyViewedPage = addAndMoveToFront((a, b) => a.id === b.id);
    return {
        ...state,
        recentlyViewedPages: addRecentlyViewedPage({
            label: action.payload.label,
            url: `/dashboard/${action.payload.id}`,
            id: action.payload.id,
            type: 'Dashboard',
        }, state.recentlyViewedPages, maxNumberPagesToSave),
    };
}), on(setRecentlyViewedApplication, (state, action) => {
    if (!state) {
        return state;
    }
    const addRecentlyViewedPage = addAndMoveToFront((a, b) => a.id === b.id);
    return {
        ...state,
        recentlyViewedPages: addRecentlyViewedPage({
            label: action.payload.name,
            url: action.payload.url,
            id: action.payload.id,
            icon: action.payload.icon,
            type: 'Application',
        }, state.recentlyViewedPages, maxNumberPagesToSave),
    };
}), on(deleteRecentlyViewedApplication, (state, action) => {
    if (!state) {
        return state;
    }
    return {
        ...state,
        recentlyViewedPages: state.recentlyViewedPages.filter(app => app.id !== action.payload.id),
    };
}), on(deleteRecentlyViewedDashboards, (state, action) => {
    if (!state) {
        return state;
    }
    const recentlyViewedPages = state.recentlyViewedPages.filter(({ id }) => (id ? !action.payload.includes(id) : true));
    const recentlyViewedDashboards = state.recentlyViewedDashboards.filter(dashboardId => action.payload.some(id => id === dashboardId));
    return {
        ...state,
        recentlyViewedDashboards: recentlyViewedDashboards,
        recentlyViewedPages: recentlyViewedPages,
    };
}), on(updateDashboardTitleInRecentlyViewed, (state, action) => {
    if (!state) {
        return state;
    }
    const addRecentlyViewedDashboard = addAndMoveToFront((a, b) => a === b);
    const recentlyViewedPages = addAndMoveToFront((a, b) => a.id === b.id);
    return {
        ...state,
        recentlyViewedDashboards: addRecentlyViewedDashboard(action.dashboardId, state.recentlyViewedDashboards, maxNumberPagesToSave),
        recentlyViewedPages: recentlyViewedPages({
            label: action.newDashboardName,
            url: `/dashboard/${action.dashboardId}`,
            id: action.dashboardId,
            type: 'Dashboard',
        }, state.recentlyViewedPages, maxNumberPagesToSave),
    };
}), on(addPinnedApplication, (state, action) => {
    if (!state) {
        return state;
    }
    const addRecentlyPinnedApp = addAndMoveToBack((a, b) => a.id === b.id);
    return {
        ...state,
        pinnedApplications: addRecentlyPinnedApp({
            id: action.payload.id,
            name: action.payload.name,
            version: action.payload.version,
            description: action.payload.description,
            description_jp: action.payload.description_jp,
            icon: action.payload.icon,
            url: action.payload.url,
        }, state.pinnedApplications),
    };
}), on(deletePinnedApplication, (state, action) => {
    if (!state) {
        return state;
    }
    return {
        ...state,
        pinnedApplications: state.pinnedApplications.filter(app => app.id !== action.payload.id),
    };
}), on(logout, () => undefined));

const initialState$2 = undefined;
const authUserReducer = createReducer(initialState$2, on(authenticateSuccess, (state, { payload }) => payload), on(logout, () => undefined));
function reducer$2(state, action) {
    return authUserReducer(state, action);
}
const getName$1 = (state) => (state ? state.cn : undefined);
const getAuthenticationStrategy$1 = (state) => (state ? state.authenticationStrategy : undefined);
const getGroups$1 = (state) => (state ? state.groups : []);
const getEmails$1 = (state) => (state ? state.emails : []);

/* eslint-disable @typescript-eslint/no-unused-vars */
const initialState$1 = undefined;
const reducer$1 = createReducer(initialState$1, on(updateLocalStorageThemeSuccess, (state, { theme, themeModifiedAt }) => ({
    theme,
    themeModifiedAt,
})));

/* eslint-disable @typescript-eslint/no-unused-vars */
const reducers = {
    loggedIn: reducer$5,
    locale: reducer$4,
    adsUser: reducer$3,
    authUser: reducer$2,
    localStorage: reducer$1,
};
const initialState = {
    loggedIn: initialState$5,
    adsUser: initialState$3,
    authUser: initialState$2,
    locale: initialState$4,
    localStorage: initialState$1,
};
const reducer = combineReducers(reducers);
const userReducer = reducer;
const selectAuthState = createFeatureSelector('user');
const isLoggedIn = createSelector(selectAuthState, state => state.loggedIn);
const getLocale = createSelector(selectAuthState, state => state.locale);
const getAuthUser = createSelector(selectAuthState, state => state.authUser);
const hasAuthUser = createSelector(getAuthUser, authUser => authUser !== undefined);
const getUser = createSelector(selectAuthState, state => state.adsUser);
const hasUser = createSelector(getUser, user => user !== undefined);
const getLocalStorage = createSelector(selectAuthState, state => state.localStorage);
/** getResolvedUserTheme resolves the theme from both localstorage and the user theme value from mongodb
 * This is required since the user can change their theme without being logged in. When that happens,
 * the theme will be saved in localStorage.
 *
 * The selector chooses the theme based on the latest timestamp for each theme. It will return undefined
 * until we initialize a theme in localstorage.
 */
const getResolvedUserTheme = createSelector(getUser, getLocalStorage, (user, localStorage) => {
    if (!localStorage) {
        return undefined;
    }
    if (!user) {
        return localStorage.theme;
    }
    const userThemeModifiedAt = user.themeModifiedAt ?? 0;
    const localStorageThemeModifiedAt = localStorage.themeModifiedAt;
    if (localStorageThemeModifiedAt > userThemeModifiedAt) {
        return localStorage.theme;
    }
    return user.lightTheme ? 'light-theme' : 'dark-theme';
});
const getRecentlyViewedPages = createSelector(getUser, user => user?.recentlyViewedPages ?? []);
const getRecentlyViewedPagesMap = createSelector(getRecentlyViewedPages, (rps) => rps.map((rp, i) => ({ ...rp, order: i })).reduce((acc, rp) => ({ ...acc, [rp.id]: rp }), {}));
const getRecentlyViewedApplications = createSelector(getUser, user => user?.recentlyViewedPages.filter(page => page.id?.startsWith('app-')) ?? []);
const getRecentAppsForSidenav = createSelector(getRecentlyViewedApplications, (apps) => apps.map(app => ({ title: app.label, tooltip: app.label, icon: app.id, url: app.url })));
const getRecentlyViewedDashboards = createSelector(getUser, user => user?.recentlyViewedDashboards ?? []);
const getGroups = createSelector(getAuthUser, getGroups$1);
const getName = createSelector(getAuthUser, getName$1);
const getEmails = createSelector(getAuthUser, getEmails$1);
const getAuthenticationStrategy = createSelector(getAuthUser, getAuthenticationStrategy$1);
const getRecentlyPinnedApps = createSelector(getUser, user => user?.pinnedApplications ?? []);
const getPinnedAppsForSidenav = createSelector(getRecentlyPinnedApps, (apps) => apps.map(app => ({ title: app.name, tooltip: app.name, icon: app.id, url: app.url })));
const getRecentlyPinnedAppIDs = createSelector(getRecentlyPinnedApps, recentlyPinnedApps => recentlyPinnedApps?.map(el => el.id) ?? []);
const getRecentlyViewedPinnedMap = createSelector(getRecentlyPinnedApps, (rps) => rps.map((rp, i) => ({ ...rp, order: i })).reduce((acc, rp) => ({ ...acc, [rp.id]: rp }), {}));

class LocaleEffects {
    constructor() {
        this.store$ = inject((Store));
        this.actions$ = inject(Actions);
        this.userCookieService = inject(UserCookieService);
        this.setLocaleCookie$ = createEffect(() => this.actions$.pipe(ofType(setLocaleCookie), map(({ payload }) => payload), tap(locale => this.userCookieService.setLocale(locale)), map(locale => setLocaleCookieSuccess({ payload: locale }))));
        this.changeTranslationLanguage$ = createEffect(() => this.actions$.pipe(ofType(setLocaleCookieSuccess), map(({ payload }) => payload), map(locale => setLocale({ payload: locale }))));
        this.initializeLocale$ = createEffect(() => defer(() => of(this.userCookieService.getLocale())).pipe(withLatestFrom(this.store$.pipe(select(getLocale))), filter(([, storeLocale]) => storeLocale !== undefined), map(([cookieLocale, storeLocale]) => {
            if (cookieLocale && cookieLocale !== storeLocale) {
                return setLocale({ payload: cookieLocale });
            }
            return setLocale({ payload: storeLocale });
        })));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: LocaleEffects, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: LocaleEffects, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: LocaleEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/* eslint-disable @typescript-eslint/no-explicit-any */
class ZeusCsrfInterceptor {
    constructor() {
        this.userCookieService = inject(UserCookieService);
    }
    intercept(req, next) {
        const hasCsrf = this.userCookieService.hasCsrf();
        const headers = hasCsrf ? { 'zeus-csrf-token': this.userCookieService.getCsrf() } : undefined;
        const authReq = req.clone({ setHeaders: headers });
        return next.handle(authReq);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ZeusCsrfInterceptor, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ZeusCsrfInterceptor, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ZeusCsrfInterceptor, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

function onErrorRetryExpBackoff({ maxRetryAttempts, scalingDuration, includedStatusCodes, } = {
    maxRetryAttempts: 3,
    scalingDuration: 1000,
    includedStatusCodes: [503],
}) {
    return (source) => source.pipe(retryWhen(errors => errors.pipe(mergeMap((error, retryAttempt) => iif(() => retryAttempt + 1 <= (maxRetryAttempts ?? 3) &&
        (includedStatusCodes ?? [503]).some(statusCode => statusCode === error.status), of(error).pipe(delay(getExponentialBackoffDelay(retryAttempt + 1, scalingDuration ?? 1000))), throwError(error))))));
}
function getExponentialBackoffDelay(retryAttempt, scalingDuration) {
    return Math.pow(2, retryAttempt) * scalingDuration;
}

class AdsUserService {
    constructor() {
        this.httpService = inject(HttpClient);
    }
    getUser(id) {
        return this.httpService.get(`/zeus/v2/users/${id}`).pipe(onErrorRetryExpBackoff({ maxRetryAttempts: 3, scalingDuration: 1000, includedStatusCodes: [503] }), catchError(err => throwError(() => err)));
    }
    updateUser(user) {
        return this.httpService.put(`/zeus/v2/users/${user.id}`, user);
    }
    createUser(user) {
        return this.httpService.post('/zeus/v2/users', user);
    }
    checkPerm(perm, userPerm) {
        return !!(userPerm & perm);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AdsUserService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AdsUserService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AdsUserService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class AuthService {
    constructor() {
        this.http = inject(HttpClient);
        this.userCookieService = inject(UserCookieService);
    }
    login(password) {
        return this.http.post('/auth', { password });
    }
    logout() {
        this.userCookieService.removeCsrf();
        return this.http.post('/logout', {}, { observe: 'response', responseType: 'text' });
    }
    authorize() {
        return this.http.get('/zeus/auth');
    }
    isLoggedIn() {
        return this.authorize().pipe(map(Boolean), catchError(() => of(false)));
    }
    setTitanCookie(user) {
        return this.userCookieService.setTitanCookie(user);
    }
    heartbeat() {
        const body = {
            disableCsrfRenewal: false,
            events: null,
        };
        return this.http.post('/zeus/v2/heartbeat', body, { observe: 'response' });
    }
    /**
     * Checks if a page is being shown from the browser's back-forward cache.
     * If it is, then check if a user is logged out. Redirect
     * user back to login page if user has logged out.
     * Ticket: https://jira.sie.sony.com/browse/CPL-2267
     * Solution adapted from: https://web.dev/bfcache/#update-stale-or-sensitive-data-after-bfcache-restore
     * @param e PageTransitionEvent
     * @param document Document
     */
    handleUnauthorizedPageShow(e, document) {
        if (e.persisted) {
            if (!this.userCookieService.hasCsrf()) {
                document.location.reload();
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AuthService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AuthService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AuthService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/* eslint-disable @typescript-eslint/no-unused-vars */
class AuthEffects {
    constructor() {
        this.actions$ = inject(Actions);
        this.authService = inject(AuthService);
        this.adsUserService = inject(AdsUserService);
        this.userCookieService = inject(UserCookieService);
        this.store = inject((Store));
        this.userLogout$ = this.actions$.pipe(ofType(logout));
        this.logout$ = createEffect(() => this.actions$.pipe(ofType(logout), switchMap(() => this.authService.logout().pipe(map(() => logoutSuccess()), catchError((error) => of(logoutFailed({ payload: error })))))));
        this.getAdsUserOnAuthSuccess$ = createEffect(() => this.actions$.pipe(ofType(authenticateSuccess), map(({ payload }) => payload), map(userAuth => setTitanCookie({ payload: userAuth }))));
        this.login$ = createEffect(() => this.actions$.pipe(ofType(login), map(({ payload }) => payload), exhaustMap(auth => this.authService.login(auth).pipe(map(ldapUser => loginSuccess({ payload: ldapUser })), catchError((error) => of(loginFailed({ payload: error })))))));
        this.setTitanCookie$ = createEffect(() => this.actions$.pipe(ofType(setTitanCookie), map(({ payload }) => payload), tap(user => this.authService.setTitanCookie(user)), mapTo(setTitanCookieSuccess())));
        this.heartbeatAfterGettingAdsUser$ = createEffect(() => this.store.pipe(select(getUser), filter(r => !!r), switchMap(() => timer(1000).pipe(takeUntil(this.userLogout$), map(() => sendHeartbeat()))), first()));
        this.heartbeatSuccessTimer$ = createEffect(() => this.actions$.pipe(ofType(sendHeartbeatSuccess), switchMap(() => timer(60000).pipe(takeUntil(this.userLogout$), map(() => sendHeartbeat())))));
        this.heartbeatFailedRetryTimer$ = createEffect(() => this.actions$.pipe(ofType(sendHeartbeatFailed), switchMap(() => timer(20000).pipe(takeUntil(this.userLogout$), map(() => sendHeartbeat())))));
        this.checkUserLocale$ = createEffect(() => this.actions$.pipe(ofType(getAdsUserSuccess), map(({ payload }) => payload), withLatestFrom(this.store.pipe(select(getLocale))), filter(([user, locale]) => user.locale !== locale), map(([, locale]) => updateAdsUserLocale({ payload: locale }))));
        this.updateUserLocale$ = createEffect(() => this.actions$.pipe(ofType(updateAdsUserLocale), withLatestFrom(this.store.pipe(select(getUser))), map(([, user]) => user), filter(r => !!r), switchMap(user => this.adsUserService.updateUser(user).pipe(map(response => updateAdsUserSuccess({ payload: response })), catchError((error) => of(updateAdsUserFailed({ payload: error })))))));
        this.sendHeartbeat$ = createEffect(() => this.actions$.pipe(ofType(sendHeartbeat), switchMap(() => this.authService.heartbeat().pipe(map(() => sendHeartbeatSuccess()), catchError((err) => of(sendHeartbeatFailed({ payload: err })))))));
        this.heartbeatLogout$ = createEffect(() => this.actions$.pipe(ofType(sendHeartbeatFailed), map(({ payload }) => payload), 
        /*
        A status of 0 is equal to an unknown error caused when exitting hot-reload development process,
        but keeping local browser tab open, to prevent future heartbeats in this case, we logout.
    */
        filter(({ status }) => status === 401 || status === 0), mapTo(logout())));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AuthEffects, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AuthEffects, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AuthEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class AdsUserEffects {
    constructor() {
        this.actions$ = inject(Actions);
        this.store$ = inject((Store));
        this.adsUserService = inject(AdsUserService);
        this.getAdsUserOnAuthSuccess$ = createEffect(() => this.actions$.pipe(ofType(authenticateSuccess), map(({ payload }) => payload), switchMap(({ id, cn: name }) => this.adsUserService.getUser(id).pipe(map(user => getAdsUserSuccess({ payload: user })), catchError((error) => {
            const userNotFound = error.status === 404;
            const action = userNotFound
                ? createAdsUser({
                    payload: {
                        name,
                        locale: 'en',
                        schema: '/resources/users/schemas/1',
                    },
                })
                : getAdsUserFailed({ payload: error });
            return of(action);
        })))));
        this.createAdsUser$ = createEffect(() => this.actions$.pipe(ofType(createAdsUser), map(({ payload }) => payload), switchMap(user => this.adsUserService.createUser(user).pipe(mergeMap(result => [
            createAdsUserSuccess({ payload: result }),
            getAdsUserSuccess({ payload: result }),
        ]), catchError((error) => {
            return of(createAdsUserFailed({ payload: error }));
        })))));
        this.toggleTheme$ = createEffect(() => this.actions$.pipe(ofType(toggleTheme), withLatestFrom(this.store$.pipe(select(getResolvedUserTheme))), map(([, prevTheme]) => updateTheme({
            theme: prevTheme === 'light-theme' ? 'dark-theme' : 'light-theme',
            themeModifiedAt: new Date().getTime(),
        }))));
        this.reconcileMongoTheme$ = createEffect(() => this.actions$.pipe(ofType(getAdsUserSuccess), withLatestFrom(this.store$.pipe(select(getResolvedUserTheme)), this.store$.pipe(select(getUser))), filter(([, resolvedTheme]) => !!resolvedTheme), filter(([, resolvedTheme, user]) => {
            const userTheme = user?.lightTheme ? 'light-theme' : 'dark-theme';
            return userTheme !== resolvedTheme;
        }), map(([, resolvedTheme]) => updateAdsTheme({ theme: resolvedTheme, themeModifiedAt: new Date().getTime() }))));
        this.onUpdateTheme$ = createEffect(() => this.actions$.pipe(ofType(updateTheme), map(({ theme, themeModifiedAt }) => updateAdsTheme({ theme, themeModifiedAt }))));
        this.updateAdsUserTheme$ = createEffect(() => this.actions$.pipe(ofType(updateAdsTheme), withLatestFrom(this.store$.pipe(select(getUser))), map(([, user]) => user), filter(r => !!r), switchMap(user => this.adsUserService.updateUser(user).pipe(map(adsUser => updateAdsUserSuccess({ payload: adsUser })), catchError((error) => {
            return of(updateAdsUserFailed({ payload: error }));
        })))));
        this.updateAdsUserRecentlyViewed$ = createEffect(() => this.actions$.pipe(ofType(setRecentlyViewedDashboard, setRecentlyViewedApplication, deleteRecentlyViewedDashboards, updateDashboardTitleInRecentlyViewed, deleteRecentlyViewedApplication), withLatestFrom(this.store$.pipe(select(getUser))), map(([, user]) => user), filter(r => !!r), switchMap(user => this.adsUserService.updateUser(user).pipe(map(adsUser => updateAdsUserSuccess({ payload: adsUser })), catchError((error) => {
            return of(updateAdsUserFailed({ payload: error }));
        })))));
        this.updatePinnedApplications$ = createEffect(() => this.actions$.pipe(ofType(addPinnedApplication, deletePinnedApplication), withLatestFrom(this.store$.pipe(select(getUser))), map(([, user]) => user), filter(r => !!r), switchMap(user => this.adsUserService.updateUser(user).pipe(map(adsUser => updateAdsUserSuccess({ payload: adsUser })), catchError((error) => {
            return of(updateAdsUserFailed({ payload: error }));
        })))));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AdsUserEffects, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AdsUserEffects, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AdsUserEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @description
 * A guard that checks if the user is logged in before navigating to a route.
 * You can place this guard in the app router.
 * See https://angular.io/guide/router#preventing-unauthorized-access
 * for how to use this guard.
 *
 * @docs-public
 */
class AuthGuard {
    constructor() {
        this.store$ = inject((Store));
        this.authService = inject(AuthService);
    }
    canActivate(next, state) {
        const hasAuthUser$ = this.store$.pipe(select(hasAuthUser));
        const alreadyAuthorized$ = hasAuthUser$.pipe(filter(r => !!r));
        const authUser$ = hasAuthUser$.pipe(filter(r => !r), exhaustMap(() => this.authService.authorize().pipe(map(authUser => {
            this.store$.dispatch(authenticateSuccess({ payload: authUser }));
            return true;
        }), catchError((err) => {
            this.store$.dispatch(authenticateFailed({ payload: state.url }));
            return of(false);
        }))));
        const authorized$ = authUser$.pipe(map(r => !!r));
        return merge(alreadyAuthorized$, authorized$).pipe(first());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AuthGuard, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AuthGuard, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: AuthGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class LocalStorageService {
    constructor() {
        this.document = inject(DOCUMENT);
    }
    getItem(key) {
        return this.document.defaultView?.localStorage.getItem(key) ?? undefined;
    }
    setItem(key, value) {
        return this.document.defaultView?.localStorage.setItem(key, value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: LocalStorageService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: LocalStorageService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: LocalStorageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class TitanLocalThemeService {
    constructor() {
        this.localStorage = inject(LocalStorageService);
        this.TITAN_THEME_KEY = 'titan-user-theme';
        this.TITAN_THEME_MODIFIED_KEY = 'titan-user-theme-modified-at';
        this.TITAN_THEMES = { 'light-theme': true, 'dark-theme': true };
        this.DEFAULT_THEME = 'dark-theme';
    }
    isTitanTheme(val) {
        return val in this.TITAN_THEMES;
    }
    getUserThemeValue() {
        const themeVal = this.localStorage.getItem(this.TITAN_THEME_KEY) ?? '';
        if (this.isTitanTheme(themeVal)) {
            return themeVal;
        }
        return this.DEFAULT_THEME;
    }
    getUserThemeModifiedAt() {
        const numString = this.localStorage.getItem(this.TITAN_THEME_MODIFIED_KEY) ?? '0';
        return parseInt(numString, 10);
    }
    setUserThemeValue(val) {
        if (this.isTitanTheme(val)) {
            this.localStorage.setItem(this.TITAN_THEME_KEY, val);
        }
    }
    setUserThemeModifiedAt(val) {
        this.localStorage.setItem(this.TITAN_THEME_MODIFIED_KEY, val.toString());
    }
    getUserTheme() {
        return {
            theme: this.getUserThemeValue(),
            modifiedAt: this.getUserThemeModifiedAt(),
        };
    }
    /** Saves the user theme value and modified timestamp to browser localstorage */
    setUserTheme(theme, modifiedAt) {
        this.setUserThemeValue(theme);
        this.setUserThemeModifiedAt(modifiedAt);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanLocalThemeService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanLocalThemeService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanLocalThemeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class LocalStorageEffects {
    constructor() {
        this.actions$ = inject(Actions);
        this.store$ = inject((Store));
        this.titanLocalThemeService = inject(TitanLocalThemeService);
        this.initializeTheme$ = createEffect(() => this.actions$.pipe(ofType(loadInitialTheme), map(() => this.titanLocalThemeService.getUserTheme()), map(({ theme, modifiedAt }) => updateLocalStorageTheme({ theme, themeModifiedAt: modifiedAt }))));
        this.updateLocalTheme$ = createEffect(() => this.actions$.pipe(ofType(updateLocalStorageTheme), tap(({ theme, themeModifiedAt }) => {
            this.titanLocalThemeService.setUserTheme(theme, themeModifiedAt);
        }), map(({ theme, themeModifiedAt }) => updateLocalStorageThemeSuccess({ theme, themeModifiedAt }))));
        this.onUpdateTheme$ = createEffect(() => this.actions$.pipe(ofType(updateTheme), map(({ theme, themeModifiedAt }) => updateLocalStorageTheme({ theme, themeModifiedAt }))));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: LocalStorageEffects, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: LocalStorageEffects, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: LocalStorageEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class UserRootModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: UserRootModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.2", ngImport: i0, type: UserRootModule, imports: [UserCookieModule, i1.EffectsFeatureModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: UserRootModule, providers: [
            {
                provide: HTTP_INTERCEPTORS,
                useClass: ZeusCsrfInterceptor,
                multi: true,
            },
            AdsUserService,
            AuthService,
            AuthGuard,
        ], imports: [UserCookieModule, EffectsModule.forFeature([AuthEffects, LocaleEffects, AdsUserEffects, LocalStorageEffects])] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: UserRootModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [UserCookieModule, EffectsModule.forFeature([AuthEffects, LocaleEffects, AdsUserEffects, LocalStorageEffects])],
                    providers: [
                        {
                            provide: HTTP_INTERCEPTORS,
                            useClass: ZeusCsrfInterceptor,
                            multi: true,
                        },
                        AdsUserService,
                        AuthService,
                        AuthGuard,
                    ],
                }]
        }] });
/** @docs-public */
class UserModule {
    static forRoot() {
        return {
            ngModule: UserRootModule,
            providers: [],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: UserModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.2", ngImport: i0, type: UserModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: UserModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: UserModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                }]
        }] });

class UserCookieTestingModule {
    static overrideCookieService(value = {}) {
        return {
            ngModule: UserCookieTestingModule,
            providers: [
                {
                    provide: CookieService,
                    useValue: value,
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: UserCookieTestingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.2", ngImport: i0, type: UserCookieTestingModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: UserCookieTestingModule, providers: [UserCookieService] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: UserCookieTestingModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [UserCookieService],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AdsUserService, AuthGuard, AuthService, LocalStorageService, TitanLocalThemeService, UserActionTypes, UserCookieModule, UserCookieService, UserCookieTestingModule, UserModule, UserRootModule, addAndMoveToBack, addAndMoveToFront, addPinnedApplication, authenticate, authenticateFailed, authenticateSuccess, createAdsUser, createAdsUserFailed, createAdsUserSuccess, deletePinnedApplication, deleteRecentlyViewedApplication, deleteRecentlyViewedDashboards, getAdsUser, getAdsUserFailed, getAdsUserSuccess, getAuthUser, getAuthenticationStrategy, getEmails, getGroups, getLocale, getName, getPinnedAppsForSidenav, getRecentAppsForSidenav, getRecentlyPinnedAppIDs, getRecentlyPinnedApps, getRecentlyViewedDashboards, getRecentlyViewedPages, getRecentlyViewedPagesMap, getRecentlyViewedPinnedMap, getResolvedUserTheme, getUser, hasAuthUser, hasUser, initialState, isLoggedIn, loadInitialTheme, login, loginAloy, loginAloyInitiate, loginFailed, loginSuccess, logout, logoutSuccess, selectAuthState, sendHeartbeat, sendHeartbeatFailed, sendHeartbeatSuccess, sendHeartbeatUnauthorized, setLandingUrlToSessionStorageAction, setLocale, setLocaleCookie, setLocaleCookieSuccess, setLocaleSuccess, setRecentlyViewedApplication, setRecentlyViewedDashboard, setTitanCookie, setTitanCookieSuccess, setUsername, toggleTheme, updateAdsUser, updateAdsUserFailed, updateAdsUserSuccess, updateDashboardTitleInRecentlyViewed, updateTheme, userReducer };
